// Helper utilities for working with APP_TYPES from MongoDB

import { apptypes, AppType } from '../../../environments/apptypes';
import { webddn } from '../../../environments/webddn';

export type { AppType };

/**
 * Get the current app type configuration
 */
export function getCurrentAppType(): AppType | undefined {
  const currentType = webddn.APP_TYPE;
  return apptypes.find(at => at.type === currentType);
}

/**
 * Get a specific app type by type string
 */
export function getAppTypeByType(type: string): AppType | undefined {
  return apptypes.find(at => at.type === type);
}

/**
 * Get all available app types
 */
export function getAllAppTypes(): AppType[] {
  return apptypes;
}

/**
 * Check if current app is of a specific type
 */
export function isAppType(type: string): boolean {
  return webddn.APP_TYPE === type;
}

/**
 * Get configuration value for current app type
 */
export function getAppTypeConfig<T = any>(key: string, defaultValue?: T): T | undefined {
  const currentAppType = getCurrentAppType();
  if (currentAppType?.config && key in currentAppType.config) {
    return currentAppType.config[key] as T;
  }
  return defaultValue;
}

/**
 * Check if a feature is enabled for current app type
 */
export function isFeatureEnabled(feature: string): boolean {
  const currentAppType = getCurrentAppType();
  if (!currentAppType?.config) return false;

  // Check for explicit boolean flags
  if (feature in currentAppType.config) {
    return !!currentAppType.config[feature];
  }

  return false;
}

/**
 * Get display text for current app type
 */
export function getAppTypeText(key: string, defaultText: string = ''): string {
  return getAppTypeConfig<string>(key, defaultText) || defaultText;
}

// Export specific checks for common app types
export const isWebApp = () => isAppType('web');
export const isSportApp = () => isAppType('sport');
export const isDDNApp = () => isAppType('ddn');
export const isHealthApp = () => isAppType('health');
export const isTransportApp = () => isAppType('transport');
export const isEcommerceApp = () => isAppType('ecommerce');
export const isOpticalApp = () => isAppType('optical');
export const isCustomApp = () => isAppType('custom');
export const isAssistantApp = () => isAppType('assistant');
export const isAiApp = () => isAppType('ai');